import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Button from '@/components/button/Button';
import { useUserService } from '@/data/services/useUserService';
import Logo from '@/assets/logo.svg';
import BannerLogin from '@/assets/banner_login.svg';
import { theme } from '@/styles/theme';
import { styles } from './styles';
import TextField from '@/components/textField/TextField';
import WarningField from '@/components/warningField/WarningField';
import { AppError } from '@/utils/AppError';
import { StorageHandler } from '@/data/storage/StorageHandler';
import { getFormattedLanguage } from '@/utils/formatedLanguage';
import CustomPopup from '@/components/customPopup/CustomPopup';
import { useSnackbarStoreComponent } from '@/stores/useSnackbar';

interface LoginFormInputs {
  email: string;
  password: string;
}

export const Login = () => {
  const { t } = useTranslation('auth');
  const language = getFormattedLanguage();
  const schema = yup.object().shape({
    email: yup.string().email(t('email_invalid')).required(t('email_required')),
    password: yup.string().required(t('password_required')),
  });
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormInputs>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();
  const { signIn } = useUserService();
  const { showSnackbar } = useSnackbarStoreComponent();
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [rememberMe, setRememberMe] = useState<boolean>(
    StorageHandler.rememberMeActive()
  );
  const [email, setEmail] = useState('');
  const [showPopup, setShowPopUp] = useState(false);
  const location = useLocation();
  const { showSuccessCode } = location.state || {};

  const handleWarningRightButtonAction = () => {
    setShowWarning(false);
  };

  const onSubmit = (data: LoginFormInputs) => {
    setLoading(true);
    StorageHandler.setRememberMe(rememberMe);
    signIn(data.email, data.password, language)
      .then(() => {
        navigate('/home');
      })
      .catch((error: AppError) => {
        if (error.message.includes('not confirmed')) {
          setEmail(data.email);
          setShowPopUp(true);
          return;
        }
        setShowWarning(true);
        setWarningMessage(t(error.message));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    StorageHandler.clearTimerCountdown();

    if (showSuccessCode) {
      showSnackbar(t('success_token'), 'success', '360px');
      navigate('/', { replace: true, state: {} });
    }
  }, []);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.content}>
        <Box
          component="form"
          sx={styles.form}
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit(onSubmit)()}
          aria-label={t('login_form')}
        >
          <Box sx={styles.header}>
            <img src={Logo} alt="Logo" />
            <Box
              component="img"
              src={BannerLogin}
              alt="Banner Login"
              sx={{
                width: { xs: '90%', sm: '70%', md: '50%' },
                maxWidth: 400,
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Controller
            name="email"
            control={control}
            defaultValue={localStorage.getItem('userEmail') || ''}
            render={({ field }) => (
              <TextField
                {...field}
                title="E-mail"
                required
                disabled={loading}
                error={!!errors.email}
                helperText={errors.email?.message}
                onValueChange={(value) => field.onChange(value)}
                placeholder={t('email_placeholder')}
                aria-label={t('email')}
                aria-required="true"
              />
            )}
          />
          <br />
          <br />
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                title={t('password')}
                required
                disabled={loading}
                error={!!errors.password}
                helperText={errors.password?.message}
                onValueChange={(value) => field.onChange(value)}
                placeholder={t('password_placeholder')}
                hasAdornmentEnd
                aria-label={t('password')}
                aria-required="true"
              />
            )}
          />

          <Box sx={styles.options}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  disabled={loading}
                  onChange={(e) => {
                    setRememberMe(e.target.checked);
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: theme.palette.primary.main,
                    },
                  }}
                  tabIndex={0}
                />
              }
              label={t('remember_me')}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '1.2rem',
                  fontWeight: 600,
                },
              }}
            />
            <Typography
              component="a"
              sx={styles.link}
              onClick={() => navigate('/forgot-password')}
              tabIndex={0}
            >
              {t('forgot_password')}
            </Typography>
          </Box>

          <WarningField
            description={warningMessage}
            isHidden={!showWarning}
            setShowWarning={setShowWarning}
            status="warning"
            rightButtonTitle={t('close')}
            rightButtonAction={handleWarningRightButtonAction}
          />

          <Box sx={styles.buttonContainer}>
            <Button
              title={loading ? '' : t('login_button')}
              type="submit"
              disable={!isValid || loading}
              loading={loading}
              tabIndex={0}
              onClick={handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                e.key === 'Enter';
              }}
            />

            <Button
              title={t('create_account')}
              onClick={() => navigate('/signUp')}
              disable={loading}
              outlined
              tabIndex={0}
            />
            <CustomPopup
              title={t(
                'signInPersonalInfo:create_account_email_already_registered'
              )}
              description={t(
                'signInPersonalInfo:login_account_email_already_registered_description'
              )}
              popupHidden={!showPopup}
              imageHidden={true}
              closeHidden={true}
              closeAction={() => setShowPopUp(false)}
              buttonTitleArray={[
                t(
                  'signInPersonalInfo:create_account_email_already_registered_change_email'
                ),
                t(
                  'signInPersonalInfo:create_account_email_already_registered_verify_email'
                ),
              ]}
              buttonOnlyTextArray={[true, false]}
              buttonOutlinedArray={[true, false]}
              buttonColorArray={['inherit', 'error']}
              buttonActionArray={[
                () => setShowPopUp(false),
                () => {
                  setShowPopUp(false);
                  navigate('/forgot-password', {
                    state: { locationEmail: email },
                  });
                },
              ]}
              buttonLoadingArray={[false, loading]}
              isDisabled={loading}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
